import React, { useEffect } from 'react';
import { Message } from 'talkplus-sdk/dist/model/types';
import { ReactionEnum, ReactionType } from '@domain/constant/chat/ReactionType';

export interface ReactionProps {
  reaction: ReactionType;
}

export type ReactionStateType = {
  type: ReactionType;
  count: number;
  isActive: boolean;
}[];

type UseChatReactProps = {
  message: Message;
  userId?: string;
};

// 채팅 리액션 상태를 관리하는 로직입니다. 클라이언트 상태 관리
const useChatReactionState = ({ message, userId }: UseChatReactProps) => {
  // talkplus를 통한 메세지별 리액션 정보를 갱신 할 수 없어서, 본인이 선택한 리액션에 대해서만 상태를 관리
  const [reactionsState, setReactionsState] = React.useState<ReactionStateType>(
    (Object.keys(ReactionEnum) as ReactionType[]).map((type) => {
      return {
        type,
        count: 0,
        isActive: false,
      };
    }),
  );

  const isReaction = reactionsState.some((reaction) => reaction.count > 0);

  useEffect(() => {
    const reactions = message?.reactions || {};
    const entries = Object.entries(reactions) as [];
    entries.forEach((value: [ReactionType, string[]]) => {
      const type = value[0];
      const count = value?.[1]?.length;
      const isActive = value?.[1]?.includes(userId || '');

      setReactionsState((prev) => {
        return prev.map((reaction) => {
          if (reaction.type === type) {
            return {
              type,
              count,
              isActive,
            };
          }
          return reaction;
        });
      });
    });
  }, [message]);

  // 리액션을 하나만 선택하기 위한 로직
  const addReactionState = ({ reaction, callback }: { callback: () => void } & ReactionProps) => {
    // 새로 반영할 리액션 상태
    const reactions = reactionsState.map((prevReaction) => {
      if (prevReaction.type !== reaction && prevReaction.isActive) {
        return {
          ...prevReaction,
          count: prevReaction.count - 1,
          isActive: false,
        };
      }

      if (prevReaction.type === reaction) {
        return {
          ...prevReaction,
          count: prevReaction.count + 1,
          isActive: true,
        };
      }

      return prevReaction;
    });

    setReactionsState(reactions);

    callback();
  };

  const removeReactionState = ({ reaction, callback }: { callback: () => void } & ReactionProps) => {
    // 리액션이 0개면 return
    if (reactionsState.find((prevReaction) => prevReaction.type === reaction)?.count === 0) return;

    setReactionsState((prev) => {
      return prev.map((prevReaction) => {
        if (prevReaction.type === reaction) {
          return {
            ...prevReaction,
            count: prevReaction.count - 1,
            isActive: false,
          };
        }
        return prevReaction;
      });
    });

    callback();
  };

  return {
    reactionsState,
    addReactionState,
    removeReactionState,
    isReaction: isReaction,
  };
};

export default useChatReactionState;
